import { useEffect, useRef, useState, type ReactNode } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { ScreenBackdrop } from './ScreenBackdrop';
import { WalletHeader } from './WalletHeader';
import { DefiWalletCard } from './DefiWalletCard';
import { CardsCarousel } from './CardsCarousel';
import { TransactionsList } from './TransactionsList';
import { CardsScreen } from './CardsScreen';
import { SendFlow } from './SendFlow';
import { ReceiveFlow } from './ReceiveFlow';
import { SwapFlow } from './SwapFlow';
import { ProfileScreen } from './ProfileScreen';
import { TxHistoryScreen } from './TxHistoryScreen';
import { CoinInfoScreen } from './CoinInfoScreen';
import { StakeScreen } from './StakeScreen';
import { WalletMainScreen } from './WalletMainScreen';
import { PushScreen } from './PushScreen';
import { ThreeDsModal } from './ThreeDsModal';
import { CardTopUpManualSheet } from './CardTopUpManualSheet';
import { type Coin, COINS } from './TokenSheet';
import { BottomNav, type Tab } from './BottomNav';
import { useCards, useWarmCardAccounts } from '../../hooks/useCards';
import { useMe } from '../../hooks/useMe';
import { useStaking } from '../../hooks/useStaking';
import { useTelegram } from '../../hooks/useTelegram';
import { useRechargeSettlements } from '../../hooks/useRechargeSettlements';
import { useWithdrawSettlements } from '../../hooks/useWithdrawSettlements';
import { type Card } from '../../lib/cards';
import { parseTokenStartParam, readTelegramStartParam } from '../../lib/startParam';
import { Confetti } from './Confetti';
import { PresaleScreen } from './PresaleScreen';
import { IncptCoinScreen } from './IncptCoinScreen';
import { IncptSendScreen } from './IncptSendScreen';
import { IncptReceiveScreen } from './IncptReceiveScreen';

type HomeTabProps = {
  onSend: () => void;
  onReceive: () => void;
  onSeeAll: () => void;
  onSelectCoin: (coin: Coin) => void;
  onCardClick: (cardId: string) => void;
  onAddCard: () => void;
  onIncpt: () => void;
};

function HomeTab({
  onSend,
  onReceive,
  onSeeAll,
  onSelectCoin,
  onCardClick,
  onAddCard,
  onIncpt,
}: HomeTabProps) {
  return (
    <div className="relative flex min-h-full flex-col">
      {/* Header glow only — white canvas continues under the sheet / nav /
          home-indicator so the page stays white to the bottom edge. */}
      <ScreenBackdrop height={480} fadeStart={72} canvas="#ffffff" />

      <div className="relative z-10 flex min-h-full flex-1 flex-col">
        <WalletHeader onSend={onSend} onReceive={onReceive} />

        {/* White sheet under the header — rounded top, through the nav. */}
        <div className="relative mt-7 flex min-h-0 flex-1 flex-col">
          <div
            aria-hidden
            className="pointer-events-none absolute inset-x-0 top-0 bottom-0 rounded-t-[40px] bg-white"
          />
          <div className="nav-pad relative flex flex-1 flex-col gap-8 rounded-t-[40px] px-4 pt-6">
            <DefiWalletCard onSelectCoin={onSelectCoin} onIncpt={onIncpt} />
            <CardsCarousel onCardClick={onCardClick} onAddCard={onAddCard} />
            <TransactionsList max={5} onSeeAll={onSeeAll} />
          </div>
        </div>
      </div>
    </div>
  );
}

type Pushed = 'send' | 'receive' | 'swap' | 'txHistory' | 'presale' | 'incpt' | 'incptSend' | 'incptReceive' | null;

const TAB_ORDER: Tab[] = ['home', 'cards', 'wallet', 'stats', 'profile'];

export function WalletScreen() {
  const qc = useQueryClient();
  const { cards } = useCards();
  // Prepare per-template Banana users in the background so Issue usually opens
  // on an already-approved account.
  useWarmCardAccounts();
  // Hydrate i18n from the saved profile language (not Telegram/system locale).
  const { links } = useMe();
  const { userId } = useTelegram();
  // Prefetch staking so the tab opens warm; the product gate only hides vaults,
  // never the tab itself.
  useStaking();
  // Background: wait for Banana CARD_RECHARGE_RESULT via our intent status, then
  // refresh balance once. Must live above TopUpSheet so closing it doesn't cancel.
  const { toast: rechargeToast, dismiss: dismissRechargeToast } = useRechargeSettlements();
  const { toast: withdrawToast } = useWithdrawSettlements();
  const successToast =
    rechargeToast?.kind === 'success'
      ? rechargeToast
      : withdrawToast?.kind === 'success'
        ? withdrawToast
        : null;
  const [tab, setTab] = useState<Tab>(() => {
    if (!import.meta.env.DEV) return 'home';
    const requested = new URLSearchParams(window.location.search).get('tab');
    if (
      requested === 'home' ||
      requested === 'cards' ||
      requested === 'wallet' ||
      requested === 'stats' ||
      requested === 'profile'
    ) {
      return requested;
    }
    return 'home';
  });
  const [pushed, setPushed] = useState<Pushed>(null);
  const [coinInfo, setCoinInfo] = useState<Coin | null>(null);
  // Coin to pre-select in the Send / Receive / Swap flows (set from a coin detail).
  const [sendCoin, setSendCoin] = useState<Coin | null>(null);
  const [receiveCoin, setReceiveCoin] = useState<Coin | null>(null);
  const [swapCoin, setSwapCoin] = useState<Coin | null>(null);
  // Card to scroll to when switching to the cards tab from the home screen.
  const [activeCardId, setActiveCardId] = useState<string | null>(null);
  // Open the issue-card sheet once the cards tab mounts (from home "+").
  const [openIssue, setOpenIssue] = useState(false);

  // Deep link: `t.me/<bot>/app?startapp=token_USDT_trx` (or `token_INCPT`).
  const tokenDeepLinkConsumed = useRef(false);
  useEffect(() => {
    if (tokenDeepLinkConsumed.current) return;
    const target = parseTokenStartParam(readTelegramStartParam());
    if (!target) return;
    tokenDeepLinkConsumed.current = true;
    if (target.kind === 'incpt') {
      setPushed('incpt');
      return;
    }
    const coin = COINS.find((c) => c.symbol === target.symbol && c.chain === target.chain);
    if (coin) setCoinInfo(coin);
  }, []);

  // Left-to-right order of the bottom-nav tabs — drives the swipe direction.
  const index = TAB_ORDER.indexOf(tab);
  const prevIndex = useRef(index);
  const firstRender = useRef(true);
  const fromRight = index >= prevIndex.current;
  useEffect(() => {
    prevIndex.current = index;
    firstRender.current = false;
  }, [index]);

  // Freeze the animation at mount so a later re-render (e.g. balances loading)
  // can't flip the direction and replay it → no bounce / double animation.
  const initialAnim = firstRender.current
    ? 'none'
    : fromRight
      ? 'tab-slide-right'
      : 'tab-slide-left';

  // No opaque shell fill — ScreenBackdrop is portaled to body, and a
  // bg-surface here would cover it (including the status-bar strip).
  return (
    <div className="relative min-h-full w-full overflow-x-hidden">
      <TabPage key={tab} anim={initialAnim}>
        {tab === 'home' && (
          <HomeTab
            onSend={() => {
              setSendCoin(null);
              setPushed('send');
            }}
            onReceive={() => {
              setReceiveCoin(null);
              setPushed('receive');
            }}
            onSeeAll={() => setPushed('txHistory')}
            onSelectCoin={(c) => setCoinInfo(c)}
            onCardClick={(cardId) => {
              setActiveCardId(cardId);
              setTab('cards');
            }}
            onIncpt={() => setPushed('incpt')}
            onAddCard={() => {
              setActiveCardId(null);
              setOpenIssue(true);
              setTab('cards');
            }}
          />
        )}
        {tab === 'cards' && (
          <CardsScreen
            onSeeAll={() => setPushed('txHistory')}
            activeCardId={activeCardId}
            openIssue={openIssue}
            onOpenIssueConsumed={() => setOpenIssue(false)}
          />
        )}
        {tab === 'profile' && <ProfileScreen />}
        {tab === 'stats' && <StakeScreen />}
        {tab === 'wallet' && (
          <WalletMainScreen
            onSend={() => {
              setSendCoin(null);
              setPushed('send');
            }}
            onReceive={() => {
              setReceiveCoin(null);
              setPushed('receive');
            }}
            onSwap={() => {
              setSwapCoin(null);
              setPushed('swap');
            }}
            onSelectCoin={(c) => setCoinInfo(c)}
            onIncpt={() => setPushed('incpt')}
          />
        )}
      </TabPage>
      <BottomNav active={tab} onChange={setTab} obscured={!!pushed || !!coinInfo} />

      {/* Telegram-style pushed screens (slide in/out from the right) */}
      <PushScreen open={!!coinInfo}>
        {coinInfo && (
          <CoinInfoScreen
            coin={coinInfo}
            onClose={() => setCoinInfo(null)}
            onSend={(c) => {
              setSendCoin(c);
              setCoinInfo(null);
              setPushed('send');
            }}
            onReceive={(c) => {
              setReceiveCoin(c);
              setCoinInfo(null);
              setPushed('receive');
            }}
            onSwap={(c) => {
              setSwapCoin(c);
              setCoinInfo(null);
              setPushed('swap');
            }}
          />
        )}
      </PushScreen>

      {pushed === 'send' && (
        <SendFlow coin={sendCoin ?? undefined} onClose={() => setPushed(null)} />
      )}

      {pushed === 'swap' && (
        <SwapFlow coin={swapCoin ?? undefined} onClose={() => setPushed(null)} />
      )}

      <PushScreen
        open={
          pushed === 'incpt' ||
          pushed === 'incptSend' ||
          pushed === 'incptReceive' ||
          pushed === 'presale'
        }
      >
        <IncptCoinScreen
          onClose={() => setPushed(null)}
          onPresale={() => setPushed('presale')}
          onSend={() => setPushed('incptSend')}
          onReceive={() => setPushed('incptReceive')}
        />
      </PushScreen>

      {pushed === 'incptSend' && (
        <div className="push-enter fixed inset-0 z-50 overflow-y-auto bg-holo">
          <IncptSendScreen onClose={() => setPushed('incpt')} />
        </div>
      )}
      {pushed === 'incptReceive' && (
        <div className="push-enter fixed inset-0 z-50 overflow-y-auto bg-holo">
          <IncptReceiveScreen onClose={() => setPushed('incpt')} />
        </div>
      )}

      <PushScreen open={pushed === 'presale'}>
        <PresaleScreen onClose={() => setPushed('incpt')} />
      </PushScreen>

      <PushScreen open={pushed === 'txHistory'}>
        <TxHistoryScreen onClose={() => setPushed(null)} />
      </PushScreen>

      {pushed === 'receive' && (
        <ReceiveFlow coin={receiveCoin ?? undefined} onClose={() => setPushed(null)} />
      )}

      {/* 3DS / wallet OTP — global so it appears on any tab when a webhook lands. */}
      {(() => {
        const pending3ds = cards.find(
          (c) => c.pendingSecurementId != null || c.pendingOtpValue != null,
        );
        if (!pending3ds) return null;
        return (
          <ThreeDsModal
            card={pending3ds}
            onDone={() => {
              // Optimistic clear only — don't invalidate here (races swipe/decline
              // and restores pending before the server has dropped it).
              qc.setQueriesData<{ cards: Card[] }>({ queryKey: ['cards'] }, (prev) => {
                if (!prev?.cards) return prev;
                return {
                  ...prev,
                  cards: prev.cards.map((c) =>
                    c.id !== pending3ds.id
                      ? c
                      : {
                          ...c,
                          pendingSecurementId: null,
                          pendingSecurementAmount: null,
                          pendingSecurementMethod: null,
                          pendingOtpValue: null,
                        },
                  ),
                };
              });
            }}
          />
        );
      })()}

      {successToast && (
        <div className="fixed inset-0 z-[70] pointer-events-none">
          <Confetti />
          <div className="fixed inset-x-0 top-0 z-[75] flex justify-center px-4 pt-[calc(var(--tg-safe-top)_+_1.25rem)]">
            <div className="animate-toast flex items-center gap-3 rounded-[20px] bg-ink px-5 py-4 text-white shadow-[0px_16px_40px_rgba(0,0,0,0.28)]">
              <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-[#34c759]">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                  <path
                    d="M5 12.5l4.5 4.5L19 7"
                    stroke="white"
                    strokeWidth="2.6"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </span>
              <div className="flex flex-col">
                <span className="text-[15px] font-semibold leading-tight">{successToast.message}</span>
                {successToast.amount != null && (
                  <span className="text-[13px] leading-tight text-white/70">
                    $
                    {Number.isInteger(successToast.amount)
                      ? successToast.amount
                      : successToast.amount.toFixed(2)}
                  </span>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {withdrawToast?.kind === 'error' && (
        <div className="pointer-events-none fixed inset-x-0 top-0 z-[70] flex justify-center px-4 pt-[calc(var(--tg-safe-top)_+_1rem)]">
          <div className="animate-toast rounded-[18px] bg-ink px-5 py-3 text-[14px] font-medium text-white shadow-[0px_16px_40px_rgba(0,0,0,0.28)]">
            {withdrawToast.message}
            {withdrawToast.amount != null && (
              <span className="ml-2 text-white/70">
                $
                {Number.isInteger(withdrawToast.amount)
                  ? withdrawToast.amount
                  : withdrawToast.amount.toFixed(2)}
              </span>
            )}
          </div>
        </div>
      )}

      <CardTopUpManualSheet
        open={rechargeToast?.kind === 'error'}
        onClose={dismissRechargeToast}
        telegramId={userId}
        at={rechargeToast?.at ?? new Date()}
        amount={rechargeToast?.amount}
        supportTelegram={links?.supportTelegram}
      />
    </div>
  );
}

/** A tab page whose slide animation is captured once at mount (keyed by tab),
 *  so re-renders never restart/flip it. */
function TabPage({ anim, children }: { anim: string; children: ReactNode }) {
  const [name] = useState(anim);
  return (
    <div className="tab-page min-h-full" style={{ animationName: name }}>
      {children}
    </div>
  );
}
